<template>
	<CDialog
		:title="dialogTitle"
		:visible="isVisible"
		:is-loading="isLoading"
		custom-class="dialog--narrow"
		@closed="$emit('closed')"
	>
		<CDeleteForms
			v-if="!isLoading"
			:form-names="formNames"
			:entries="totalEntries"
			:allow-use-form-name="allowUseFormName"
			:connected-forms="connectedForms"
			@confirm-deletion="handleDelete"
			@cancel="$emit('closed')"
		/>
	</CDialog>
</template>

<script lang="ts">
	import type { PropType } from 'vue';
	// eslint-disable-next-line no-restricted-imports -- needed for Vue.delete, not a global registration
	import Vue, { defineComponent, ref, set, watch } from 'vue';
	import CDeleteForms from 'src/components/app/DeleteForms.vue';
	import CDialog from 'src/components/dialogs/Dialog.vue';
	import { globalStore } from 'src/stores/global-store';
	import { getFormDetails } from '@cognitoforms/api/services/form-details-service';
	import type { LookupFormReference } from '@cognitoforms/types/server-types/services/forms/lookup-form-reference';

	export default defineComponent({
		name: 'DeleteFormsDialog',
		components: {
			CDialog,
			CDeleteForms
		},
		props: {
			show: {
				type: Boolean,
				required: true
			},
			formIds: {
				type: Array as PropType<string[]>,
				required: true
			},
			allowUseFormName: {
				type: Boolean,
				default: false
			}
		},
		emits: ['closed', 'deleted'],
		setup(props, { emit }) {
			const isLoading = ref(true);
			const isVisible = ref(props.show);

			// Lists keyed by formId.
			const formNames = ref<Record<string, string>>({});
			const connectedForms = ref<Record<string, LookupFormReference[]>>({});

			const dialogTitle = ref('');
			const totalEntries = ref(0);

			const handleDelete = () => {
				props.formIds.forEach((formId) => {
					globalStore.deleteForm(formId);
				});

				isVisible.value = false;
				emit('deleted');
			};

			const initialize = () => {
				if (!props.show) {
					isVisible.value = false;
					return;
				}

				connectedForms.value = {};
				isVisible.value = true;
				isLoading.value = true;

				Promise.all(
					props.formIds.map(async (formId) => {
						const formDetails = await getFormDetails(formId);
						set(connectedForms.value, formId, formDetails.ReferencedBy);
					})
				).then(() => {
					formNames.value = Object.fromEntries(props.formIds.map(formId => [formId, globalStore.forms[formId].Name]));
					dialogTitle.value = (props.formIds.length > 1 ? 'Delete Forms?' : `Delete ${globalStore.forms[props.formIds[0]].Name}?`);
					totalEntries.value = props.formIds.reduce((acc, formId) => acc + globalStore.forms[formId].Entries, 0);

					for (const id in connectedForms.value) {
						if (connectedForms.value[id].length === 0)
							Vue.delete(connectedForms.value, id);
						else
							set(connectedForms.value, id, connectedForms.value[id].filter(c => c.FormId !== id));
					}

					isLoading.value = false;
				});
			};

			watch(() => props.show, initialize, { immediate: true });

			return {
				isLoading,
				isVisible,
				dialogTitle,
				formNames,
				totalEntries,
				connectedForms,
				handleDelete
			};
		}
	});
</script>